/* eslint-disable react/no-unstable-nested-components */

import { Box, Flex, IconButton, Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { Close, Download } from '@/client/components/icons/ContinuIcons';

import CertificateV2 from './CertificateV2';
import ReactToPrint from 'react-to-print';
import type { UserCertificate } from '@/client/types/content/UserCertificate';
import { format } from 'date-fns';
import { useRef } from 'react';

interface CertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  certificate: UserCertificate;
}

export default function CertificateModal({ isOpen, onClose, certificate }: CertificateModalProps) {
  const printableRef = useRef<HTMLDivElement>(null);

  return (
    <Modal size="full" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent backgroundColor="white" margin={0}>
        {/* Action buttons */}
        <Flex position="fixed" top={4} right={4} zIndex={10} gap={2}>
          <ReactToPrint
            documentTitle={`Certificate ${format(new Date(certificate.completedDate), 'M-d-yyyy')}`}
            trigger={() => (
              <IconButton
                backgroundColor="white"
                borderRadius="full"
                aria-label="Print certificate"
                icon={<Download />}
                variant="ghost"
                size="lg"
                color="neutral.800"
                _hover={{
                  backgroundColor: 'white',
                  color: 'neutral.1200',
                }}
                boxShadow="0px 2px 8px rgba(0, 0, 0, 0.1)"
              />
            )}
            content={() => printableRef.current}
            pageStyle={`
              @page { 
                size: landscape; 
                margin: 0; 
              }
              @media print {
                body { 
                  margin: 0; 
                  padding: 0; 
                }
                * { 
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
            `}
          />

          <IconButton
            backgroundColor="white"
            borderRadius="full"
            aria-label="Close modal"
            icon={<Close />}
            variant="ghost"
            size="lg"
            color="neutral.800"
            onClick={onClose}
            _hover={{
              backgroundColor: 'white',
              color: 'neutral.1200',
            }}
            boxShadow="0px 2px 8px rgba(0, 0, 0, 0.1)"
          />
        </Flex>

        <Box
          ref={printableRef}
          width="full"
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={8}
          sx={{
            '@media print': {
              width: '11in',
              height: '8.5in',
              minWidth: '11in',
              minHeight: '8.5in',
              maxWidth: '11in',
              maxHeight: '8.5in',
              padding: 0,
              margin: 0,
              display: 'block',
              background: 'none',
            },
          }}
        >
          <CertificateV2 certificate={certificate} variant="modal" />
        </Box>
      </ModalContent>
    </Modal>
  );
}
